import { useInvariableEffect } from '@servicetitan/cp-react-hooks';
import { ContentLink, LoadStatus } from '@servicetitan/cp-ui';
import { Banner, BodyText, Button, Divider, Form, Label, Stack } from '@servicetitan/design-system';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import { ContactMethod } from 'api';
import { observer } from 'mobx-react';
import { FC, Fragment, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TermsOfServiceLink } from '../../../legal-agreement/components/terms-of-service-link';
import { AuthInviteStore } from '../../stores/invite.store';
import { LoginResult, PortalAuthStore } from '../../stores/portal-auth.store';
import {
    useAuthCodeParams,
    useAuthHistory,
    useAuthInviteQueryParams,
} from '../../utils/use-auth-history';
import { AuthLayout } from '../layout/auth.layout';
import { AuthHeadline } from '../layout/components';
import { passwordError, PasswordInput } from '../ui/password.input';

export const AuthInviteNew: FC<{ code?: string }> = observer(({ code }) => {
    const [{ canSubmit, contactMethod, form, passwordValidator, submit, submitStatus }] =
        useDependencies(AuthInviteStore);
    const { agree, confirm, email, password } = form.$;
    const contactLabel = contactMethod === ContactMethod.PhoneNumber ? 'Phone' : 'Email';

    const history = useAuthHistory();
    const isSubmitting = submitStatus === LoadStatus.Loading;

    return (
        <Fragment>
            {submitStatus === LoadStatus.Error && (
                <Banner status="warning" icon title="Registration failed" className="m-b-2">
                    Please contact us for help.
                </Banner>
            )}

            <AuthHeadline
                title="Create Account"
                description="Finish setting up your account by creating a password."
            />

            <Form>
                <Fragment>
                    <Label>{contactLabel}</Label>
                    <Stack alignItems="center" className="m-b-2 p-b-half">
                        <BodyText className="t-truncate">{email.value}</BodyText>
                    </Stack>
                    <input name="email" type="hidden" value={email.value} />
                </Fragment>

                <PasswordInput
                    data-cy="password"
                    label="Password"
                    value={password.value}
                    onChange={password.onChangeHandler}
                    error={passwordError(passwordValidator, password.error)}
                    disabled={isSubmitting}
                    autoComplete="new-password"
                />

                <PasswordInput
                    data-cy="confirm-password"
                    label="Confirm password"
                    value={confirm.value}
                    onChange={confirm.onChangeHandler}
                    error={confirm.error}
                    disabled={isSubmitting}
                />

                <Form.Checkbox
                    data-cy="terms-agreement"
                    value={agree.value}
                    onChange={agree.onChangeHandler}
                    error={agree.error}
                    disabled={isSubmitting}
                    label={
                        <BodyText>
                            I agree to{' '}
                            <TermsOfServiceLink primary>terms of service</TermsOfServiceLink>.
                        </BodyText>
                    }
                />

                <Button
                    width="full"
                    primary
                    onClick={submit}
                    loading={isSubmitting}
                    disabled={!canSubmit}
                >
                    Create Account
                </Button>

                <BodyText className="m-t-3 ta-center">
                    Have an account?{' '}
                    <ContentLink primary href={history.href('loginOnInvite', { code })}>
                        Sign In
                    </ContentLink>
                </BodyText>
            </Form>
        </Fragment>
    );
});

export const AuthInviteExisted: FC<{ code?: string; createNewUser(): void }> = observer(
    ({ code, createNewUser }) => {
        const [{ authorizedUser, mapStatus, mapUser }] = useDependencies(AuthInviteStore);
        const isMapping = mapStatus === LoadStatus.Loading;

        return (
            <Fragment>
                <AuthHeadline
                    title="Update Account"
                    description="You’ve been invited to access a new workspace."
                />

                <Button width="full" primary onClick={() => mapUser(code)} loading={isMapping}>
                    Continue as {authorizedUser?.name}
                </Button>

                <Divider className="m-y-2">
                    <BodyText size="small" className="p-x-1" subdued>
                        OR
                    </BodyText>
                </Divider>

                <Button width="full" onClick={createNewUser} loading={isMapping}>
                    Create New Account
                </Button>
            </Fragment>
        );
    }
);

const AuthInviteProvider = provide({ singletons: [AuthInviteStore] })(({ children }) => children);

export const AuthInviteComponent = observer(() => {
    const [
        {
            init,
            loadError,
            loadStatus,
            submitResult,
            submitStatus,
            /*
             * mapStatus,
             * setShowMergeOption,
             * showMergeOption,
             */
        },
        { mfaCodeLength, mfaCodeLifetimeMinutes, mfaResendCooldownSeconds, mfaSessionToken },
    ] = useDependencies(AuthInviteStore, PortalAuthStore);

    const history = useAuthHistory();
    const rootNavigate = useNavigate();
    const { code } = useAuthCodeParams();
    const { auto } = useAuthInviteQueryParams();

    useInvariableEffect(() => {
        if (!code) {
            history.replace('login');
        } else {
            init(code, auto);
        }
    });

    useEffect(() => {
        if (loadStatus === LoadStatus.Error) {
            if (loadError === 'already_registered') {
                history.replace('login', undefined, { info: 'exists' });
            } else {
                history.replace('register', undefined, { info: 'invite_expired' });
            }
        }
    }, [history, loadError, loadStatus]);

    useEffect(() => {
        if (submitStatus === LoadStatus.Ok /* || mapStatus === LoadStatus.Ok*/) {
            if (submitResult === LoginResult.MfaRequired && mfaSessionToken) {
                rootNavigate(history.href('mfa', undefined, { keepSigned: 'false' }), {
                    replace: true,
                    state: {
                        mfaSessionToken,
                        mfaCodeLength,
                        mfaCodeLifetimeMinutes,
                        mfaResendCooldownSeconds,
                    },
                });
            } else {
                rootNavigate('/', { replace: true });
            }
        }
    }, [
        rootNavigate,
        submitStatus,
        submitResult,
        mfaSessionToken,
        mfaCodeLength,
        mfaCodeLifetimeMinutes,
        mfaResendCooldownSeconds,
        history,
    ]);

    const isLoading = loadStatus !== LoadStatus.Ok;

    return (
        <AuthLayout loading={isLoading}>
            {/* {showMergeOption ? (
                <AuthInviteExisted code={code} createNewUser={() => setShowMergeOption(false)} />
            ) : (
                <AuthInviteNew code={code} />
            )} */}
            <AuthInviteNew code={code} />
        </AuthLayout>
    );
});

export const AuthInvite = () => (
    <AuthInviteProvider>
        <AuthInviteComponent />
    </AuthInviteProvider>
);
